import { useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import BoltIcon from "@mui/icons-material/Bolt";

const StatItem = ({ label, value }) => (
  <MDBox 
    p={2} 
    borderRadius="lg" 
    sx={{ 
      backgroundColor: 'grey.100',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 2
      }
    }}
  >
    <MDTypography variant="caption" color="text" fontWeight="medium" mb={1} display="block">
      {label}
    </MDTypography>
    <MDTypography variant="h4" fontWeight="bold">
      {value}
    </MDTypography>
  </MDBox>
);

StatItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

function TransactionDetails({ transaction, open, onClose }) {
  if (!transaction) return null;

  const formatNumber = (num) => Number(num).toFixed(2);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          overflowY: "auto",
          borderRadius: 2,
        },
      }}
    >
      <MDBox position="relative">
        {/* Header */}
        <MDBox
          position="relative"
          p={3}
          sx={{
            background: `#ffffff`,
            color: "white",
            borderTopLeftRadius: 2,
            borderTopRightRadius: 2,
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "white",
            }}
          >
            <CloseIcon />
          </IconButton>

          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <MDBox
                width="4rem"
                height="4rem"
                bgcolor="rgba(255,255,255,0.1)"
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <BoltIcon sx={{ fontSize: 32, color: "white" }} />
              </MDBox>
            </Grid>
            <Grid item>
              <MDTypography variant="h3" color="white" mb={0.5}>
                {transaction.powerStationName}
              </MDTypography>
              <MDTypography variant="body2" color="white" opacity={0.8}>
                تاريخ القراءة: {new Date(transaction.createdAt).toLocaleString("ar-IQ")}
              </MDTypography>
            </Grid>
          </Grid>
        </MDBox>

        <DialogContent sx={{ p: 0 }}>
          {/* Main Stats */}
          <MDBox p={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <StatItem
                  label="توليد الطاقة"
                  value={formatNumber(transaction.powerGenerator)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <StatItem
                  label="الهدف"
                  value={formatNumber(transaction.target)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <StatItem
                  label="التردد"
                  value={formatNumber(transaction.frequency)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <StatItem
                  label="ضغط الغاز"
                  value={formatNumber(transaction.gasPressure)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <StatItem
                  label="تدفق الغاز (م³/ساعة)"
                  value={formatNumber(transaction.gasVolume)}
                />
              </Grid>
            </Grid>
          </MDBox>

          {/* ST Readings */}
          {transaction.stReadings?.map((stReading, stIndex) => (
            <MDBox key={stIndex} px={3} pb={3}>
              <Card>
                {/* ST Header */}
                <MDBox 
                  p={3} 
                  sx={{ 
                    borderBottom: '2px solid',
                    borderColor: 'info.main',
                    background: (theme) => `linear-gradient(to right, ${theme.palette.grey[100]}, ${theme.palette.white})`
                  }}
                >
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <MDBox display="flex" alignItems="center">
                        <MDBox
                          mr={2}
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                            backgroundColor: 'info.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <BoltIcon sx={{ color: 'white', fontSize: 28 }} />
                        </MDBox>
                        <MDTypography variant="h4" fontWeight="bold">
                          {stReading.stName}
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <MDBox
                        p={2}
                        borderRadius="lg"
                        sx={{ backgroundColor: 'white', boxShadow: 1 }}
                      >
                        <MDTypography variant="caption" color="text" mb={1} display="block">
                          قيمة التوربين
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="bold" color="info">
                          {formatNumber(stReading.stValue)}
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <MDBox
                        p={2}
                        borderRadius="lg"
                        sx={{ backgroundColor: 'white', boxShadow: 1 }}
                      >
                        <MDTypography variant="caption" color="text" mb={1} display="block">
                          القيمة المتوقعة
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="bold" color="dark">
                          {formatNumber(stReading.expectedSTValue)}
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  </Grid>
                </MDBox>

                {/* GT Readings Table */}
                <MDBox p={3}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            width: '20%',
                            borderBottom: '2px solid',
                            borderColor: 'info.main',
                            py: 2
                          }}
                        >
                            <MDBox display="flex" alignItems="center">
                            <MDTypography variant="subtitle2" fontWeight="bold">
                            التوربين
                          </MDTypography>
                            </MDBox>
                        </TableCell>

                      
                        
                        <TableCell 
                          sx={{ 
                            width: '20%',
                            borderBottom: '2px solid',
                            borderColor: 'info.main',
                            py: 2
                          }}
                        >
                            <MDBox display="flex" alignItems="center">
                            <MDTypography variant="subtitle2" fontWeight="bold">
                            القيمة
                            </MDTypography>
                            </MDBox>
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            width: '100%',
                            borderBottom: '2px solid',
                            borderColor: 'info.main',
                            py: 2
                          }}
                        >
                        <MDBox display="flex" alignItems="center">
                          <MDTypography variant="subtitle2" fontWeight="bold">
                            الحالة
                          </MDTypography>
                        </MDBox>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                    <TableBody>
                      {stReading.gtReadings?.map((gtReading, gtIndex) => (
                        <TableRow 
                          key={gtIndex}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'grey.50'
                            },
                            '& td': {
                              borderColor: 'grey.200',
                              py: 2
                            }
                          }}
                        >
                          <TableCell>
                            <MDBox display="flex" alignItems="center">
                              <MDBox
                                mr={2}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: '8px',
                                  backgroundColor: 'grey.100',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <BoltIcon sx={{ color: 'text.info', fontSize: 20 }} />
                              </MDBox>
                              <MDTypography variant="button" fontWeight="medium">
                                {gtReading.gtName}
                              </MDTypography>
                            </MDBox>
                          </TableCell>
                          <TableCell>
                            <MDTypography variant="h6" fontWeight="bold" color="info">
                              {formatNumber(gtReading.gtValue)}
                            </MDTypography>
                          </TableCell>
                          <TableCell>
                            <MDBadge
                              badgeContent={gtReading.statusName}
                              color={gtReading.statusName.toLowerCase() === "running" ? "success" : "error"}
                              variant="gradient"
                              size="md"
                              container
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </MDBox>
              </Card>
            </MDBox>
          ))}
        </DialogContent>
      </MDBox>
    </Dialog>
  );
}

TransactionDetails.propTypes = {
  transaction: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TransactionDetails;
